import { map } from 'lodash-es';
import type { ReactElement } from 'react';
import { lazy, memo } from 'react';
import { useSelector } from 'react-redux';
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router-dom';

import type { RootState } from 'store';

import { AuthenticatedRoute } from 'common/auth/auth_route';
import { DeviceHandoffOwnerTabGate } from 'common/deviceHandoff/components/DeviceHandoffOwnerTabGate';
import { DeviceHandoffRouteGate } from 'common/deviceHandoff/components/DeviceHandoffRouteGate';
import { DeviceHandoffDialog } from 'common/deviceHandoff/DeviceHandoffDialog';
import { deviceHandoffRoutes } from 'common/deviceHandoff/deviceHandoffRoutes';
import { DEVICE_HANDOFF_PATH } from 'common/deviceHandoff/state/constants';
import { FeatureFlagsSynchronizer } from 'common/featureFlags/FeatureFlagsSynchronizer';
import { useFxIntegrationInitialization } from 'common/featureFlags/hooks/useFxIntegrationInitialization';
import { NavigationBlockerProvider } from 'common/routing/blocking/NavigationBlockerContext';
import UserTypeSelectionPage from 'module/auth/login/userTypeSelection.page';
import OktaAuthProvider from 'module/auth/okta/OktaAuthProvider';

import { AuthUserLandingPage } from './AuthUserLandingPage';
import { routeDefinitions } from './routeDefinitions';

const LoginPage = lazy(() => import('module/auth/login/LoginPage/LoginPage'));

const ErrorComponent = (): ReactElement => <div>404 Not Found</div>;

const RootLayout = (): ReactElement => {
    // needed for feature flag triggered re-rendering
    useFxIntegrationInitialization();

    return (
        <OktaAuthProvider>
            <FeatureFlagsSynchronizer>
                <NavigationBlockerProvider>
                    <DeviceHandoffOwnerTabGate>
                        <DeviceHandoffRouteGate>
                            <Outlet />
                        </DeviceHandoffRouteGate>
                    </DeviceHandoffOwnerTabGate>
                </NavigationBlockerProvider>
            </FeatureFlagsSynchronizer>
        </OktaAuthProvider>
    );
};

const HomeOrLoginRoute = (): ReactElement => {
    const activeUser = useSelector((state: RootState) => state.auth?.activeUser);

    if (activeUser) {
        return <AuthUserLandingPage />;
    }

    return <UserTypeSelectionPage />;
};

const PatientLoginRoute = (): ReactElement => {
    const activeUser = useSelector((state: RootState) => state.auth?.activeUser);

    if (activeUser) {
        return <Navigate to="/" replace />;
    }
    return <LoginPage />;
};

const router = createBrowserRouter([
    {
        element: <RootLayout />,
        children: [
            { path: '/', element: <HomeOrLoginRoute /> },
            { path: '/login', element: <HomeOrLoginRoute /> },
            { path: '/patientLogin', element: <PatientLoginRoute /> },
            { path: DEVICE_HANDOFF_PATH, element: <DeviceHandoffDialog />, children: deviceHandoffRoutes },
            ...map(routeDefinitions, (route) => {
                if (route.redirects) {
                    return {
                        path: route.path,
                        element: <Navigate to={route.redirects} replace />,
                    };
                }

                if (!route.permissions?.length && !route.role?.length) {
                    const AsyncComponent = route.component;
                    return {
                        path: route.path,
                        element: <AsyncComponent />,
                    };
                }

                return {
                    path: route.path,
                    element: (
                        <AuthenticatedRoute
                            permissions={route.permissions}
                            role={route.role}
                            component={route.component}
                            accessRoute={route}
                        />
                    ),
                };
            }),
            { path: '*', element: <ErrorComponent /> },
        ],
    },
]);

const AppRouter = (): ReactElement => <RouterProvider router={router} />;

export default memo(AppRouter);
